// One match, end to end: binds the Match rules to the 3D scene, the HUD, the local player's input,
// CPU drivers, and (in multiplayer) the room. Solo practice is just a host with no room.

import { Color3 } from '@babylonjs/core/Maths/math.color';
import type { GameEngine } from './engine';
import type { World } from './world';
import { VehicleLibrary, type VehicleView } from './vehicle';
import type { PeopleLibrary } from './people';
import { ChaseCamera } from './camera';
import { PlayerInput } from './input';
import { PassengerReactions, REACTION } from './passenger-reactions';
import { Markers, Minimap } from './markers';
import { SIM_DT } from './config';
import { Match, type MatchEvent, type PlayerKind, type PlayerState } from '../sim/match';
import { Navigator } from '../sim/nav';
import { BotPlayer } from '../sim/botplayer';
import { PASSENGERS } from '../sim/fares';
import type { CarState, ImpactContact } from '../sim/car';
import type { HudView, Screens } from '../ui/screens';
import type { RoomClient, RosterEntry } from '../net/client';
import { Sfx } from './audio';
import { DisasterDirector, disasterCopy, type DisasterSpec } from '../sim/disasters';
import { Streets, KARMA } from '../sim/streets';
import { DEFAULT_FLAGS, type Flags } from '../sim/flags';
import { StreetFx } from './streetfx';
import type { DebugMenu } from './debug';
import { QualityManager } from './quality';
import { SceneInstrumentation } from '@babylonjs/core/Instrumentation/sceneInstrumentation';

export interface SessionOptions {
  mode: 'solo' | 'host' | 'client';
  seed: number;
  durationSec: number;
  flags?: Flags;
  localId: string;
  roster: RosterEntry[];
  bots: number;
  net?: RoomClient;
  botSkill?: number;
  onEnd: (players: PlayerState[]) => void;
}

const BOT_NAMES = ['Robo-Cab', 'Autopilot Al', 'Tin Lizzie', 'Cabbot', 'Meter Maid', 'Rusty', 'Nav-Man', 'Bleep'];

export class Session {
  readonly match: Match;
  readonly nav: Navigator;
  readonly bots: BotPlayer[] = [];
  readonly views = new Map<string, VehicleView>();
  readonly input: PlayerInput;
  readonly cam: ChaseCamera;
  readonly markers: Markers;
  readonly minimap: Minimap;
  readonly hud: HudView;
  readonly sfx = new Sfx();
  readonly director: DisasterDirector;
  readonly streets: Streets;
  readonly streetFx: StreetFx;
  debug: DebugMenu | null = null;
  quality!: QualityManager;
  instr!: SceneInstrumentation;
  private warnSpec: DisasterSpec | null = null;
  private remoteTargets = new Map<string, CarState>();
  private acc = 0; private last = performance.now();
  private netTick = 0; private snapTick = 0;
  private running = true;
  private offerHeadline = '';
  private renderCb: () => void;
  private centreUntil = 0;
  private readonly passengerReactions = new PassengerReactions();
  private passengerBadDriving = false;
  private speakingFare: number | null = null;
  private bannerUntil = 0;
  private ownedIds: string[] = [];
  private forced: { steer: number; brake: number; nitro: boolean; throttle?: number } | null = null;

  constructor(readonly g: GameEngine, readonly world: World, readonly vehicles: VehicleLibrary, readonly people: PeopleLibrary | null, readonly screens: Screens, readonly opts: SessionOptions) {
    const { scene, engine, shadows } = g;
    const w = world.data;
    this.match = new Match(world.ground, w.play_area, w.taxi_ranks, w.landmarks, w.bus_stops, { durationSec: opts.durationSec, seed: opts.seed, countdownSec: 3, flags: opts.flags ?? DEFAULT_FLAGS });
    this.match.authority = opts.mode !== 'client';
    this.nav = new Navigator(world.ground);

    // players from the roster (humans, the AI player, host-owned CPUs), then local practice bots
    let rankIdx = 0;
    for (const r of opts.roster) {
      const kind: PlayerKind = r.id === opts.localId ? 'human' : r.kind === 'human' || r.kind === 'ai' ? 'remote' : 'bot';
      this.match.addPlayer(r.id, r.name, r.colour, kind, rankIdx++);
    }
    if (!this.match.players.has(opts.localId)) this.match.addPlayer(opts.localId, 'You', 'F2B90C', 'human', rankIdx++);
    if (opts.mode !== 'client') {
      for (let i = 0; i < opts.bots; i++) {
        const id = `bot${i}`; const colour = ['E5433A', '3E8BFF', '4CC26B', '9B5DE5', 'FF7A1A', 'FF6BB5', 'F5F1E8'][i % 7];
        this.match.addPlayer(id, BOT_NAMES[i % BOT_NAMES.length], colour, 'bot', rankIdx++);
      }
      for (const p of this.match.players.values()) if (p.kind === 'bot') this.bots.push(new BotPlayer(this.match, p.id, this.nav, opts.botSkill ?? 0.55 + Math.random() * 0.4));
    }
    this.ownedIds = [...this.match.players.values()].filter((p) => p.kind !== 'remote').map((p) => p.id);

    for (const p of this.match.players.values()) {
      const tint = p.colour.toUpperCase() === 'F2B90C' ? null : Color3.FromHexString('#' + p.colour);
      const v = vehicles.spawn(p.kind === 'bot' ? 'SM_Vehicles_Taxi_02' : 'SM_Vehicles_Taxi_01', tint, shadows);
      v.feedback(p.health / p.maxHealth, 0);
      v.apply(p.car); this.views.set(p.id, v);
    }

    this.cam = new ChaseCamera(scene); this.cam.ground = world.ground; this.cam.snapTo(this.me.car);
    this.input = new PlayerInput(engine.getRenderingCanvas()!);
    this.input.onHorn = () => { this.sfx.horn(); this.say(); this.me.hornUntil = this.match.time + 0.8; };
    this.input.onNitro = () => this.ability();
    this.hud = screens.hud();
    this.hud.onNitro = () => this.ability();
    this.hud.onHorn = () => this.input.onHorn?.();
    this.hud.setKarmaVisible(this.flags.karma);
    this.hud.onOffer = (action, id) => this.offerAction(action, id);
    this.markers = new Markers(scene, world.ground, people);
    this.minimap = new Minimap(this.hud.minimap, world.ground, world.data);
    this.hud.setMoney(0); this.hud.setHealth(100, 100); this.hud.setKarma(0);
    this.hud.setDebug(new URLSearchParams(location.search).has('debug') ? '' : null);

    this.director = new DisasterDirector(w.play_area, world.ground, w.volcano, opts.seed, opts.durationSec);
    this.streets = new Streets(world.ground, this.nav, w.play_area, w.traffic_lights, opts.seed, new URLSearchParams(location.search).has('quiet') ? 0 : 70, new URLSearchParams(location.search).has('quiet') ? 0 : 12);
    this.streets.flags = this.match.flags;
    this.streetFx = new StreetFx(scene, world.ground, vehicles, people, shadows, this.streets);

    this.instr = new SceneInstrumentation(scene); this.instr.captureFrameTime = true; this.instr.captureRenderTime = true; this.instr.captureActiveMeshesEvaluationTime = true;
    const q = new URLSearchParams(location.search).get('quality');
    this.quality = new QualityManager(g, { setCrowd: (peds, traffic, range) => { (this.streets as unknown as { pedCount: number; trafficCount: number }).pedCount = new URLSearchParams(location.search).has('quiet') ? 0 : peds; (this.streets as unknown as { trafficCount: number }).trafficCount = new URLSearchParams(location.search).has('quiet') ? 0 : traffic; this.streetFx.peopleRange = range; } }, q != null ? Number(q) as 0 | 1 | 2 | 3 : null);
    this.sfx.preload(); this.sfx.playMusic('game');
    if (opts.net) this.bindNet(opts.net);

    this.renderCb = () => this.frame();
    engine.runRenderLoop(this.renderCb);
  }

  get me(): PlayerState { return this.match.players.get(this.opts.localId)!; }

  /** Automation hook (window.cab.setInput). */
  setForcedInput(f: { steer: number; brake: number; nitro: boolean; throttle?: number } | null): void { this.forced = f; }
  get flags(): Flags { return this.match.flags; }

  // ------------------------------------------------------------------ loop
  private frame(): void {
    if (!this.running) return;
    const now = performance.now(); let dt = (now - this.last) / 1000; this.last = now;
    if (dt > 0.25) dt = 0.25;
    this.acc += dt;
    while (this.acc >= SIM_DT) { this.tick(SIM_DT); this.acc -= SIM_DT; }
    // visuals
    for (const p of this.match.players.values()) {
      const v = this.views.get(p.id); if (!v) continue;
      if (p.kind === 'remote') this.interpolateRemote(p, dt);
      v.apply(p.car);
      v.feedback(p.health / p.maxHealth, dt);
      v.root.setEnabled(p.respawnAt == null || this.match.time - (p.respawnAt - 4) < 0.05 ? true : false);
    }
    this.cam.update(this.me.car, dt);
    this.markers.update(this.match, this.me, dt);
    this.streetFx.update(this.streets, this.director, this.match.time, this.me.karma >= KARMA.saintAt ? this.me.car : null, dt, this.me.car);
    this.debug?.update(dt);
    this.quality.tick();
    this.g.scene.render();
    this.updateHud(dt);
  }

  private tick(dt: number): void {
    const me = this.me; const m = this.match;
    const inp = this.input.update(dt);
    me.input.steer = inp.steer; me.input.brake = inp.brake; me.input.nitro = inp.nitro; me.input.throttle = inp.throttle;
    if (this.forced) { me.input.steer = this.forced.steer; me.input.brake = this.forced.brake; me.input.nitro = this.forced.nitro; me.input.throttle = this.forced.throttle ?? 1; }
    const driverSteer = me.input.steer;
    for (const b of this.bots) b.step(dt);
    const owned = this.ownedIds.map((id) => m.players.get(id)!).filter(Boolean);
    // disasters: host schedules, everyone runs the timeline for the cars they own
    if (this.opts.mode !== 'client') { const spec = this.director.maybeSchedule(m.time); if (spec) this.startDisaster(spec, true); }
    const wobble = this.director.steerWobble(m.time);
    for (const p of owned) {
      if (wobble) p.input.steer = Math.max(-1, Math.min(1, p.input.steer + wobble));
      const lost = this.director.noControlUntil.get(p.id); if (lost && m.time < lost) { p.input.steer = Math.sin(m.time * 20); p.input.brake = 0; }
    }
    m.step(dt, this.ownedIds, this.streets.collisionCircles());
    if (this.flags.disasters) this.director.step(m, owned, dt);
    this.streets.step(m, owned, dt, me.car);
    for (const e of this.streets.drainEvents()) this.onStreetEvent(e);
    if (wobble && m.phase === 'playing') this.cam.addShake(0.25 * dt * 10);
    for (const e of m.drainEvents()) this.onEvent(e);
    this.updatePassengerReactions(dt, driverSteer);
    if (me.car.impact > 4) { this.cam.addShake(Math.min(1, me.car.impact / 25)); this.sfx.crash(me.car.impact); }
    if (me.car.impact > 1 && this.debug) {
      const g = this.world.ground; const fx = Math.sin(me.car.yaw), fz = Math.cos(me.car.yaw); const ax = me.car.x + fx * 1.5, az = me.car.z + fz * 1.5;
      this.debug.noteHit(me.car.x, me.car.z, me.car.impactKind, me.car.impact, `ahead cell blocked=${g.blockedAt(ax, az) ? 1 : 0} top=${g.topAt(ax, az)} surf=${g.surfaceAt(ax, az)} here surf=${g.surfaceAt(me.car.x, me.car.z)}`);
    }
    this.sfx.engine(Math.abs(me.car.speed) / me.tuning.maxSpeed, me.nitroUntil > m.time);
    this.netStep(dt);
    if (m.phase === 'ended' && this.running) this.finish();
  }

  private updatePassengerReactions(dt: number, driverSteer: number): void {
    const me = this.me, m = this.match;
    if (this.speakingFare != null && (!me.riding.some(f => f.id === this.speakingFare) || m.phase !== 'playing')) {
      this.hud.clearSpeech(); this.speakingFare = null;
    }
    const reaction = this.passengerReactions.update({
      time: m.time, dt, riding: me.riding, playing: m.phase === 'playing' && me.respawnAt == null,
      speed: me.car.speed, steer: driverSteer,
      badDriving: this.passengerBadDriving,
      disaster: this.flags.disasters ? this.director.nearbyDanger(me.car.x, me.car.z, m.time) : null,
    });
    this.passengerBadDriving = false;
    if (reaction) {
      this.speakingFare = reaction.fareId;
      this.hud.reactPassenger(reaction.text, REACTION.displayMs, reaction.event === 'badDriving');
    }
  }

  private updateHud(dt: number): void {
    const me = this.me, m = this.match, h = this.hud;
    h.setControllerHints(this.input.mode === 'controller');
    h.setMoney(me.money); h.setClock(m.timeLeft(), m.rush); h.setHealth(me.health, me.maxHealth); h.setKarma(me.karma);
    h.setFares(me.riding, me.freezeUntil > m.time); h.setSheltered(this.director.sheltered.has(me.id));
    const hasAbility = me.perks.nitro || me.perks.jet || me.perks.ghost || me.perks.meterFreeze;
    const label = me.perks.jet ? 'JUMP' : me.perks.ghost ? 'GHOST' : me.perks.meterFreeze ? 'FREEZE' : 'NITRO';
    const ready = me.perks.jet ? m.time >= me.jetReadyAt : me.perks.ghost ? m.time >= me.ghostReadyAt : me.perks.meterFreeze ? m.time >= me.freezeReadyAt : m.time >= me.nitroReadyAt;
    h.setNitro(hasAbility, ready, label);
    if (me.offer) h.updateOfferTimer(me.offer.expiresAt - m.time); else h.hideOffer();
    if (this.centreUntil && performance.now() > this.centreUntil) { h.centreText(null); this.centreUntil = 0; }
    if (this.warnSpec) {
      const left = this.warnSpec.start + this.warnSpec.warn - m.time;
      if (left <= 0) { this.warnSpec = null; h.hideBanner(); this.bannerUntil = 0; }
      else { const c = disasterCopy(this.warnSpec.kind); h.showBanner(c.big, c.from, c.tip, Math.ceil(left)); }
    } else if (this.bannerUntil && performance.now() > this.bannerUntil) { h.hideBanner(); this.bannerUntil = 0; }
    // boundary warning band
    const pa = this.world.data.play_area; const c = me.car;
    const edge = Math.min(c.x - pa.x[0], pa.x[1] - c.x, c.z - pa.z[0], pa.z[1] - c.z);
    h.setBoundaryWarning(edge < 30 ? 1 - edge / 30 : 0);
    this.minimapAcc += dt; if (this.minimapAcc > 0.1) { this.minimapAcc = 0; this.minimap.draw(m, me); }
    if (this.debugOn) h.setDebug(`fps ${Math.round(this.g.engine.getFps())} q${this.quality.level} draw ${this.instr.drawCallsCounter.current} render ${this.instr.renderTimeCounter.lastSecAverage.toFixed(1)}ms  ${(c.speed * 3.6).toFixed(0)} km/h  pos ${c.x.toFixed(0)},${c.z.toFixed(0)}  mode ${this.input.mode}\nfares waiting ${m.fares.filter((f) => f.state === 'waiting').length}  players ${m.players.size}  net ${this.opts.net ? `${this.opts.net.stats.direct}d/${this.opts.net.stats.relayed}r` : '-'}`);
  }
  private minimapAcc = 0;
  private debugOn = new URLSearchParams(location.search).has('debug');

  // ------------------------------------------------------------------ events
  private onEvent(e: MatchEvent): void {
    const h = this.hud; const mine = 'pid' in e && e.pid === this.opts.localId;
    switch (e.t) {
      case 'countdown': h.centreText(String(e.n)); this.centreUntil = performance.now() + 900; if (!this.sfx.say(`vo_countdown_${e.n}`)) this.sfx.tick(); break;
      case 'go': h.centreText('GO!'); this.centreUntil = performance.now() + 900; this.sfx.go(); this.sfx.say('vo_go'); break;
      case 'pickup': if (mine) { const pax = PASSENGERS.find((p) => p.id === e.fare.passenger)!; h.toast(`${pax.name} hopped in`, 'good'); this.sfx.pickup(); h.say(pax.lines[0]); this.speakingFare = e.fare.id; this.passengerReactions.defer(this.match.time); setTimeout(() => this.sfx.say(`vo_${e.fare.passenger}_1`), 400); } break;
      case 'dropoff': { const pl = this.match.players.get(e.pid); if (pl && this.ownedIds.includes(e.pid)) this.streets.addKarma(pl, KARMA.deliver, 'delivered on time'); } if (mine) { h.toast(`+$${e.total}${e.tip ? ` (tip $${e.tip})` : ''}${e.smooth ? ' · smooth ride' : ''}`, 'money'); this.sfx.cash(); this.offerHeadline = `+$${e.total} · NICE DRIVING`; } break;
      case 'fail': if (mine) { h.toast(e.reason === 'timeout' ? 'Too slow! Passenger stormed off' : 'Passenger lost in the explosion', 'bad'); this.sfx.fail(); } break;
      case 'explode': { const v = this.views.get(e.pid); if (v) this.sfx.explode(); if (mine) { h.centreText('KABOOM', 'red'); this.centreUntil = performance.now() + 2500; this.cam.addShake(1.5); setTimeout(() => this.sfx.say('vo_kaboom'), 600); } break; }
      case 'respawn': if (mine) { this.cam.snapTo(this.me.car); h.centreText('Back on the road', 'small'); this.centreUntil = performance.now() + 1200; } break;
      case 'offer': if (mine) { this.showOffer(); this.sfx.play('card'); } break;
      case 'upgrade': if (mine) h.toast('Upgrade installed', 'good'); break;
      case 'rush': h.showBanner('RUSH HOUR!', 'Every fare pays double', 'Sixty seconds left. Go go go!', null, 'rush'); this.bannerUntil = performance.now() + 3500; this.sfx.rush(); this.sfx.say('vo_rush'); this.sfx.playMusic('final', 0.4); break;
      case 'end': this.sfx.play('timesup'); this.sfx.say('vo_timesup'); break;
      case 'damage': {
        const view = this.views.get(e.pid), player = this.match.players.get(e.pid);
        if (view && player) { view.apply(player.car); view.hit(e.contact, e.amount); }
        if (e.contact && this.ownedIds.includes(e.pid)) this.opts.net?.sendTo(null, { t: 'scar', pid: e.pid, contact: e.contact, amount: e.amount }, true);
        if (mine) {
          h.hit(); this.cam.addShake(0.22); if (e.kind !== 'car') this.sfx.hit();
          if (e.kind !== 'disaster') this.passengerBadDriving = true;
        }
      } break;
    }
    if (this.opts.mode === 'host' && this.opts.net && ['pickup', 'dropoff', 'fail', 'explode', 'respawn', 'rush', 'countdown', 'go'].includes(e.t)) this.opts.net.sendTo(null, { t: 'ev', e: serializeEvent(e) }, true);
  }

  startDisaster(spec: DisasterSpec, broadcast: boolean): void {
    this.director.begin(spec);
    this.warnSpec = spec;
    this.sfx.alarm(); this.sfx.say(`vo_${spec.kind}`); setTimeout(() => this.sfx.disaster(spec.kind), spec.warn * 1000);
    if (broadcast && this.opts.net) this.opts.net.sendTo(null, { t: 'dis', spec }, true);
  }

  /** Debug: start a disaster of a kind right now. */
  forceDisaster(kind: DisasterSpec['kind']): void {
    const s = this.director.makeSpec(kind, this.match.time, Math.floor(Math.random() * 1e9));
    this.startDisaster(s, this.opts.mode !== 'client');
  }

  private onStreetEvent(e: ReturnType<Streets['drainEvents']>[number]): void {
    const mine = e.pid === this.opts.localId; const h = this.hud;
    if (e.t === 'karma' && mine) { if (Math.abs(e.delta) >= 2) h.toast(`${e.delta > 0 ? '+' : ''}${e.delta} karma · ${e.reason}`, e.delta > 0 ? 'good' : 'bad'); }
    else if (e.t === 'pedHit' && mine) { this.passengerBadDriving = true; this.sfx.hit(); h.say(['Wheee!', 'Hey! Watch it!', 'My groceries!', 'I was walking here!'][Math.floor(Math.random() * 4)], 1500); }
    else if (e.t === 'fined' && mine) { h.toast(`Police fine: −$${e.amount}`, 'bad'); this.sfx.fail(); }
    else if (e.t === 'police' && mine) { if (e.on) { h.toast('The police are after you!', 'bad'); this.sfx.siren(); this.sfx.say('vo_police'); } else h.toast('The police gave up', 'good'); }
  }

  private showOffer(): void {
    const me = this.me; if (!me.offer) return;
    const cost = this.match.rerollCost(me);
    this.hud.showOffer(me.offer.cards, me.owned, cost, me.money >= cost, me.offer.expiresAt - this.match.time, this.offerHeadline || 'PASSENGER DELIVERED');
  }

  private offerAction(action: 'pick' | 'skip' | 'reroll', id?: string): void {
    if (this.opts.mode === 'client' && this.opts.net) { this.opts.net.sendTo(this.opts.net.hostId, { t: 'act', a: action, id }, true); this.hud.hideOffer(); return; }
    if (action === 'pick' && id) this.match.chooseUpgrade(this.opts.localId, id);
    else if (action === 'skip') this.match.skipOffer(this.opts.localId);
    else if (action === 'reroll') { if (this.match.reroll(this.opts.localId)) this.sfx.reroll(); }
    if (this.me.offer) this.showOffer(); else this.hud.hideOffer();
  }

  private ability(): void {
    if (this.opts.mode === 'client' && this.opts.net) this.opts.net.sendTo(this.opts.net.hostId, { t: 'act', a: 'ability' }, true);
    this.match.useAbility(this.opts.localId);
    this.me.input.nitro = true; setTimeout(() => { this.me.input.nitro = false; }, 120);
  }

  private say(): void {
    const ride = this.me.riding[0]; if (!ride) return;
    const pax = PASSENGERS.find((p) => p.id === ride.passenger)!;
    const i = Math.floor(Math.random() * pax.lines.length);
    this.speakingFare = ride.id; this.passengerReactions.defer(this.match.time);
    this.hud.say(pax.lines[i]); this.sfx.say(`vo_${ride.passenger}_${i + 1}`);
  }

  // ------------------------------------------------------------------ network
  private bindNet(net: RoomClient): void {
    net.onMessage((from, msg) => {
      switch (msg.t) {
        case 'scar': {
          const pid = msg.pid as string, p = this.match.players.get(pid);
          if (!p || p.kind !== 'remote' || (from !== pid && from !== net.hostId)) break;
          const c = msg.contact as ImpactContact | undefined;
          if (!c || ![c.x, c.z, c.nx, c.nz, msg.amount].every(v => typeof v === 'number' && Number.isFinite(v)) || Math.hypot(c.nx, c.nz) < .5 || Math.abs(c.x) > 4 || Math.abs(c.z) > 5) break;
          this.views.get(pid)?.hit(c, Math.max(0, Math.min(100, msg.amount as number)));
          break;
        }
        case 'car': {
          const c = msg as unknown as CarMsg;
          const p = this.match.players.get(from); if (!p || p.kind !== 'remote') break;
          this.remoteTargets.set(from, { x: c.x, y: c.y, z: c.z, yaw: c.yaw, vx: c.vx, vy: 0, vz: c.vz, speed: c.s, airborne: !!c.ab, wheelAngle: c.wa, wheelSpin: c.ws, impact: 0, impactKind: 'none' });
          this.match.setRemoteCar(from, {}, c.hp, c.rs ?? null);
          break;
        }
        case 'cars': { // host forwards bot cars
          for (const c of (msg.list as CarMsg[])) { const p = this.match.players.get(c.id!); if (p && p.kind === 'remote') { this.remoteTargets.set(c.id!, { x: c.x, y: c.y, z: c.z, yaw: c.yaw, vx: c.vx, vy: 0, vz: c.vz, speed: c.s, airborne: !!c.ab, wheelAngle: c.wa, wheelSpin: c.ws, impact: 0, impactKind: 'none' }); p.health = c.hp; p.respawnAt = c.rs ?? null; } }
          break;
        }
        case 'snap': if (this.running && this.opts.mode === 'client' && from === net.hostId) { this.match.applySnapshot(msg.s as never, this.opts.localId); for (const e of this.match.drainEvents()) this.onEvent(e); for (const spec of (msg.d as DisasterSpec[] | undefined) ?? []) if (!this.director.active.some((a) => a.spec.id === spec.id)) this.startDisaster(spec, false); } break;
        case 'ev': if (this.opts.mode === 'client') this.onEvent(deserializeEvent(msg.e as never, this.match)); break;
        case 'dis': if (this.opts.mode === 'client') this.startDisaster(msg.spec as DisasterSpec, false); break;
        case 'act': if (this.opts.mode === 'host') { const a = msg.a as string; if (a === 'pick') this.match.chooseUpgrade(from, msg.id as string); else if (a === 'skip') this.match.skipOffer(from); else if (a === 'reroll') this.match.reroll(from); else if (a === 'ability') this.match.useAbility(from); } break;
      }
    });
    net.onRoster = (roster, hostId, left) => {
      if (left && this.match.players.has(left)) { this.match.removePlayer(left); this.views.get(left)?.dispose(); this.views.delete(left); this.remoteTargets.delete(left); }
      if (hostId === this.opts.localId && this.opts.mode === 'client') { this.opts.mode = 'host'; this.match.authority = true; this.hud.toast('You are the host now'); }
      for (const r of roster) if (!this.match.players.has(r.id)) {
        const p = this.match.addPlayer(r.id, r.name, r.colour, r.kind === 'bot' ? 'remote' : 'remote');
        const v = this.vehicles.spawn('SM_Vehicles_Taxi_01', Color3.FromHexString('#' + r.colour), this.g.shadows); v.apply(p.car); this.views.set(r.id, v);
      }
    };
  }

  private netStep(dt: number): void {
    const net = this.opts.net; if (!net) return;
    this.netTick += dt;
    if (this.netTick >= 1 / 20) {
      this.netTick = 0;
      net.sendTo(null, carMsg(this.me, null), false);
      if (this.opts.mode === 'host' && this.bots.length) net.sendTo(null, { t: 'cars', list: this.bots.map((b) => carMsg(b.player!, b.pid)) }, false);
    }
    if (this.opts.mode === 'host') { this.snapTick += dt; if (this.snapTick >= 0.25) { this.snapTick = 0; net.sendTo(null, { t: 'snap', s: this.match.snapshot(), d: this.director.activeSpecs(this.match.time) }, true); } }
  }

  private interpolateRemote(p: PlayerState, dt: number): void {
    const t = this.remoteTargets.get(p.id); if (!t) return;
    const c = p.car; const k = Math.min(1, 12 * dt);
    // dead-reckon a little, then ease toward the latest sample
    t.x += t.vx * dt; t.z += t.vz * dt;
    c.x += (t.x - c.x) * k; c.y += (t.y - c.y) * k; c.z += (t.z - c.z) * k;
    let dy = t.yaw - c.yaw; while (dy > Math.PI) dy -= 2 * Math.PI; while (dy < -Math.PI) dy += 2 * Math.PI; c.yaw += dy * k;
    c.speed = t.speed; c.wheelAngle = t.wheelAngle; c.wheelSpin += t.speed * dt / 0.35; c.vx = t.vx; c.vz = t.vz;
  }

  // ------------------------------------------------------------------ end
  private finish(): void {
    if (!this.running) return;
    // The periodic snapshot may not be due on the final tick. Flush the authoritative
    // results before stopping the loop, or guests remain in a playing match forever.
    if (this.opts.mode === 'host' && this.opts.net) {
      this.opts.net.sendTo(null, { t: 'snap', s: this.match.snapshot(), d: [] }, true);
    }
    this.running = false;
    this.sfx.stop();
    setTimeout(() => this.opts.onEnd(this.match.ranking()), 800);
  }

  dispose(): void {
    this.running = false;
    this.g.engine.stopRenderLoop(this.renderCb);
    this.input.dispose();
    for (const v of this.views.values()) v.dispose();
    this.hud.destroy();
    this.streetFx.dispose();
    this.sfx.stop();
  }
}

interface CarMsg extends Record<string, unknown> { t: 'car'; id?: string; x: number; y: number; z: number; yaw: number; vx: number; vz: number; s: number; wa: number; ws: number; ab: 0 | 1; hp: number; rs: number | null; }
function carMsg(p: PlayerState, id: string | null): CarMsg {
  const c = p.car; const r = (v: number) => Math.round(v * 100) / 100;
  return { t: 'car', ...(id ? { id } : {}), x: r(c.x), y: r(c.y), z: r(c.z), yaw: r(c.yaw), vx: r(c.vx), vz: r(c.vz), s: r(c.speed), wa: r(c.wheelAngle), ws: r(c.wheelSpin % 6.283), ab: c.airborne ? 1 : 0, hp: Math.round(p.health), rs: p.respawnAt };
}
function serializeEvent(e: MatchEvent): unknown {
  if ('fare' in e) return { ...e, fare: { id: e.fare.id, passenger: e.fare.passenger, dest: e.fare.dest, timeLeft: e.fare.timeLeft, timeAllowed: e.fare.timeAllowed } };
  return e;
}
function deserializeEvent(e: MatchEvent, m: Match): MatchEvent {
  if ('fare' in e) { const full = m.fares.find((f) => f.id === e.fare.id); if (full) return { ...e, fare: full }; }
  return e;
}
